document.addEventListener('alpine:init', () => {
  Alpine.data(
    'resultsComponent',
    (applicationId, apiKey, indexName, preloadedProducts = null) => ({
      searchQuery: '',
      results: [],
      filteredResults: [],
      isLoading: false,
      selectedSort: 'relevance',
      categories: [],
      selectedCategories: [],
      hasUrlFacets: false,
      mobileFiltersOpen: false,
      filters: {},

      // Pagination (client-side)
      // Page sizes must stay multiples of 6 — the grid is 2 columns below lg and
      // 3 from lg up, so anything else leaves a hole in the last row of every
      // full page, which reads as "no more products".
      currentPage: 0,
      itemsPerPage: 24,
      selectedResultsPerPage: 24,
      resultsPerPageOptions: [24, 36, 48, 96],
      allResults: [],
      paginatedResults: [],
      totalPages: 0,

      // Tag filters are discovered dynamically from product tags (format: "prefix:value").
      // Add entries here to override the auto-capitalized label for a known prefix.
      tagFilterLabels: {
        'ausfuehrung:': 'Ausführung',
        'einheit:': 'Passend für Einheit',
        'geraet:': 'Gerät',
        'gruppe:': 'Gruppe',
        'marke:': 'Passend für Marke',
      },
      // Internal-only prefixes that must not become filter groups.
      tagFilterHidden: ['cat:'],
      tagFilters: {},

      sortOptions: [
        { value: 'relevance', label: 'Relevanz' },
        { value: 'name-asc', label: 'Name A–Z' },
        { value: 'name-desc', label: 'Name Z–A' },
        { value: 'price-asc', label: 'Preis aufsteigend' },
        { value: 'price-desc', label: 'Preis absteigend' },
      ],

      algolia: {
        applicationId: applicationId,
        apiKey: apiKey,
        indexName: indexName,
        baseUrl: applicationId ? `https://${applicationId}.algolia.net` : null,
      },

      usePreloadedProducts: preloadedProducts !== null,
      preloadedProducts: preloadedProducts,

      init() {
        const urlParams = new URLSearchParams(window.location.search);
        this.searchQuery = urlParams.get('q') || '';
        this.applyUrlFacets(urlParams);

        if (this.usePreloadedProducts) {
          this.initializeFromPreloadedProducts();
        } else if (this.searchQuery || this.hasUrlFacets) {
          this.performSearch();
        }

        this.$watch('selectedSort', () => {
          if (this.filteredResults.length > 0) this.applyFiltersAndSort();
        });

        this.$watch('selectedResultsPerPage', () => {
          this.itemsPerPage = this.selectedResultsPerPage;
          this.currentPage = 0;
          this.updatePagination();
        });
      },

      // Seeds filters from finder deep-links: ?geraet=&marke=&type=
      // extractTagFilters() preserves these selected arrays on first build.
      applyUrlFacets(urlParams) {
        const geraet = urlParams.get('geraet') || '';
        const marke = urlParams.get('marke') || '';
        const type = urlParams.get('type') || '';

        if (type) this.selectedCategories = [type];

        const seed = {};
        if (geraet) seed['geraet:'] = { selected: [geraet] };
        if (marke) seed['marke:'] = { selected: [marke] };
        if (Object.keys(seed).length) this.tagFilters = seed;

        this.hasUrlFacets = Boolean(geraet || marke || type);
      },

      initializeFromPreloadedProducts() {
        if (!this.preloadedProducts || !Array.isArray(this.preloadedProducts)) {
          this.allResults = [];
          this.filteredResults = [];
          this.paginatedResults = [];
          return;
        }
        this.allResults = this.transformProducts(this.preloadedProducts);
        this.extractCategories();
        this.extractTagFilters();
        this.applyFiltersAndSort();
      },

      transformProducts(products) {
        const isAlgolia = products.length > 0 && products[0].objectID !== undefined;
        const list = isAlgolia ? this.dedupeAlgoliaHits(products) : products;
        return list.map((product, index) =>
          product.objectID !== undefined
            ? this.transformAlgoliaHit(product, index)
            : this.transformCustomProduct(product, index)
        );
      },

      // The Algolia index is one record per variant — collapse to one card per product
      // (keep the best-ranked variant record, collect the sibling option values on it).
      dedupeAlgoliaHits(hits) {
        const byProduct = new Map();
        hits.forEach(hit => {
          const key = hit.id || hit.objectID;
          const optionValue = hit.option1 && hit.option1 !== 'Default Title' ? hit.option1 : null;
          if (!byProduct.has(key)) {
            hit._optionValues = optionValue ? [optionValue] : [];
            byProduct.set(key, hit);
            return;
          }
          const first = byProduct.get(key);
          if (optionValue && !first._optionValues.includes(optionValue)) {
            first._optionValues.push(optionValue);
          }
        });
        return [...byProduct.values()];
      },

      shopifySuggestUrl(query, limit = 10) {
        const params = new URLSearchParams();
        params.set('q', query || '');
        params.set('resources[type]', 'product');
        params.set('resources[limit]', String(limit));
        params.set('resources[options][unavailable_products]', 'last');
        params.set('resources[options][fields]', 'title,product_type,variants.sku,vendor,tag');
        return `/search/suggest.json?${params.toString()}`;
      },

      async searchShopifyFallback(query, limit = 10) {
        const response = await fetch(this.shopifySuggestUrl(query, limit), {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`Shopify predictive search error: ${response.status}`);
        const data = await response.json();
        return data?.resources?.results?.products || [];
      },

      shopifySearchSectionUrl(query, page = 1) {
        const params = new URLSearchParams();
        params.set('q', query || '');
        params.set('type', 'product');
        params.set('section_id', 'shopify-search-fallback');
        if (page > 1) params.set('page', String(page));
        return `/search?${params.toString()}`;
      },

      parseShopifySearchSection(html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const script = doc.querySelector('[data-shopify-search-fallback-json]');
        if (!script) throw new Error('Shopify search fallback section did not return JSON');
        return JSON.parse(script.textContent || '{}');
      },

      async searchShopifyFullFallback(query) {
        const firstResponse = await fetch(this.shopifySearchSectionUrl(query), {
          headers: { Accept: 'text/html' },
        });
        if (!firstResponse.ok) throw new Error(`Shopify search fallback error: ${firstResponse.status}`);

        const firstPage = this.parseShopifySearchSection(await firstResponse.text());
        const products = firstPage.products || [];
        const pages = firstPage.pages || 1;

        if (pages > 1) {
          const remainingPages = Array.from({ length: pages - 1 }, (_, index) => index + 2);
          const pageResults = await Promise.all(remainingPages.map(async (page) => {
            const response = await fetch(this.shopifySearchSectionUrl(query, page), {
              headers: { Accept: 'text/html' },
            });
            if (!response.ok) throw new Error(`Shopify search fallback page ${page} error: ${response.status}`);
            return this.parseShopifySearchSection(await response.text()).products || [];
          }));
          pageResults.forEach(pageProducts => products.push(...pageProducts));
        }

        return products;
      },

      transformAlgoliaHit(hit, index) {
        // The Algolia index is variant-level — surface the variant and deep-link to it.
        // Multi-variant products (deduped upstream) show an "ab" min price + option list instead
        // of a single variant badge and link the default variant. An exact SKU hit is shown as that one variant.
        const variantTitle = hit.variant_title && hit.variant_title !== 'Default Title' ? hit.variant_title : null;
        const basePath = (hit.url || `/products/${hit.handle}`).split('?')[0];
        const multi = (hit.variants_count || 1) > 1 && !hit._highlightResult?.sku?.fullyHighlighted;
        const variantCount = multi ? hit.variants_count : 1;
        return {
          id: hit.objectID,
          productId: hit.id || null,
          sku: hit.sku || null,
          title: hit.title,
          variantTitle: multi ? null : variantTitle,
          variantCount: variantCount,
          optionValues: hit._optionValues || [],
          url: variantTitle && !multi ? `${basePath}?variant=${hit.objectID}` : basePath,
          oem: hit.meta?.custom?.oem_reference || null,
          fitsText: hit.meta?.custom?.passt_fuer || null,
          image: hit.product_image || hit.image,
          description: hit.body_html_safe?.slice(0, 120) + '...' || null,
          price: multi && hit.variants_min_price ? hit.variants_min_price.toFixed(2) : (hit.price ? hit.price.toFixed(2) : null),
          priceMax: multi && hit.variants_max_price ? hit.variants_max_price.toFixed(2) : null,
          compareAt: !multi && hit.compare_at_price > hit.price ? hit.compare_at_price.toFixed(2) : null,
          available: multi ? (hit.variants_inventory_count > 0 || hit.inventory_available || null) : (hit.inventory_available || null),
          product_type: hit.product_type || null,
          tags: hit.tags || [],
          position: index,
        };
      },

      formatCustomPrice(price, priceIsDecimal = false) {
        if (price === null || price === undefined || price === '') return null;
        if (priceIsDecimal) {
          const numericPrice = typeof price === 'number' ? price : parseFloat(price);
          return Number.isFinite(numericPrice) ? numericPrice.toFixed(2) : null;
        }
        if (typeof price === 'number') return (price / 100).toFixed(2);
        return price;
      },

      transformCustomProduct(product, index) {
        const variant = Array.isArray(product.variants) && product.variants.length > 0
          ? product.variants[0]
          : null;

        return {
          id: product.variantId || product.variant_id || variant?.id || product.id || product.objectID || index,
          productId: product.productId || null,
          sku: product.sku || variant?.sku || null,
          title: product.title || product.name || '',
          variantTitle: product.variantTitle || null,
          variantCount: product.variantCount || 1,
          optionValues: product.optionValues || [],
          url: product.url || `/products/${product.handle || product.id}`,
          oem: product.oem || product.meta?.custom?.oem_reference || null,
          fitsText: product.fitsText || product.meta?.custom?.passt_fuer || null,
          image: product.image || product.featured_image?.url || product.featured_image || product.product_image || null,
          description: product.description
            ? product.description.replace(/<[^>]*>/g, '').slice(0, 120) + '...'
            : (product.body ? product.body.replace(/<[^>]*>/g, '').slice(0, 120) + '...' : null),
          price: this.formatCustomPrice(product.price, product.priceIsDecimal || typeof product.price === 'string'),
          priceMax: this.formatCustomPrice(product.priceMax, product.priceIsDecimal || typeof product.priceMax === 'string'),
          compareAt: this.formatCustomPrice(product.compareAt, true),
          available: product.available !== undefined ? product.available : (product.inventory_available || null),
          product_type: product.product_type || product.type || product.category || null,
          tags: product.tags || [],
          position: product.position !== undefined ? product.position : index,
        };
      },

      extractCategories() {
        const categorySet = new Set();
        this.allResults.forEach(product => {
          if (product.product_type) categorySet.add(product.product_type);
        });
        this.categories = Array.from(categorySet).map(c => ({ name: c, value: c }));
      },

      // Scans all product tags for "prefix:value" patterns and builds filter groups.
      // New tag types are picked up automatically. Add to tagFilterLabels for a
      // human-readable name; unknown prefixes fall back to auto-capitalized.
      extractTagFilters() {
        const tagSets = {};

        this.allResults.forEach(product => {
          if (!product.tags || !Array.isArray(product.tags)) return;
          product.tags.forEach(tag => {
            const colonIdx = tag.indexOf(':');
            if (colonIdx < 1) return;
            const prefix = tag.substring(0, colonIdx + 1);
            if (this.tagFilterHidden.includes(prefix)) return;
            const value = tag.substring(colonIdx + 1).trim();
            if (!value) return;
            if (!tagSets[prefix]) tagSets[prefix] = new Set();
            tagSets[prefix].add(value);
          });
        });

        const newFilters = {};
        Object.keys(tagSets).sort().forEach(prefix => {
          const rawName = prefix.slice(0, -1);
          const name = this.tagFilterLabels[prefix]
            || (rawName.charAt(0).toUpperCase() + rawName.slice(1));
          newFilters[prefix] = {
            name,
            options: Array.from(tagSets[prefix])
              .sort((a, b) => a.localeCompare(b, 'de'))
              .map(v => ({ name: v, value: v })),
            selected: this.tagFilters[prefix]?.selected || [],
          };
        });

        this.tagFilters = newFilters;
      },

      filterResults() {
        let filtered = [...this.allResults];

        if (this.selectedCategories.length > 0) {
          filtered = filtered.filter(p => this.selectedCategories.includes(p.product_type));
        }

        Object.entries(this.tagFilters).forEach(([prefix, tagFilter]) => {
          if (tagFilter.selected.length === 0) return;
          filtered = filtered.filter(product => {
            if (!product.tags || !Array.isArray(product.tags)) return false;
            return product.tags.some(tag =>
              tag.startsWith(prefix) && tagFilter.selected.includes(tag.substring(prefix.length))
            );
          });
        });

        this.filteredResults = filtered;
      },

      applyFiltersAndSort() {
        this.filterResults();
        this.sortResults();
        this.updatePagination();
      },

      sortResults() {
        switch (this.selectedSort) {
          case 'name-asc':
            this.filteredResults.sort((a, b) => {
              const c = a.title.localeCompare(b.title, 'de');
              return c !== 0 ? c : a.position - b.position;
            });
            break;
          case 'name-desc':
            this.filteredResults.sort((a, b) => {
              const c = b.title.localeCompare(a.title, 'de');
              return c !== 0 ? c : a.position - b.position;
            });
            break;
          case 'price-asc':
            this.filteredResults.sort((a, b) => {
              const c = parseFloat(a.price) - parseFloat(b.price);
              return c !== 0 ? c : a.position - b.position;
            });
            break;
          case 'price-desc':
            this.filteredResults.sort((a, b) => {
              const c = parseFloat(b.price) - parseFloat(a.price);
              return c !== 0 ? c : a.position - b.position;
            });
            break;
          case 'relevance':
          default:
            this.filteredResults.sort((a, b) => a.position - b.position);
            break;
        }
      },

      updatePagination() {
        const total = this.filteredResults.length;
        this.totalPages = Math.ceil(total / this.itemsPerPage);
        this.totalResults = total;
        if (this.currentPage >= this.totalPages) {
          this.currentPage = Math.max(0, this.totalPages - 1);
        }
        const start = this.currentPage * this.itemsPerPage;
        this.paginatedResults = this.filteredResults.slice(start, start + this.itemsPerPage);
      },

      goToPage(page) {
        if (page >= 0 && page < this.totalPages && page !== this.currentPage) {
          this.currentPage = page;
          this.updatePagination();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      },

      nextPage() { this.goToPage(this.currentPage + 1); },
      previousPage() { this.goToPage(this.currentPage - 1); },

      getPageNumbers() {
        const pages = [];
        const current = this.currentPage;
        const total = this.totalPages;

        if (total > 0) pages.push(0);

        const start = Math.max(1, current - 2);
        const end = Math.min(total - 1, current + 2);

        if (start > 1) pages.push('...');
        for (let i = start; i <= end; i++) {
          if (i !== 0 && i !== total - 1) pages.push(i);
        }
        if (end < total - 2) pages.push('...');
        if (total > 1) pages.push(total - 1);

        return pages;
      },

      async performSearch() {
        if (!this.searchQuery.trim() && !this.hasUrlFacets) return;
        this.isLoading = true;

        const loadResults = (products) => {
          this.allResults = this.transformProducts(products);
          this.extractCategories();
          this.extractTagFilters();
          this.applyFiltersAndSort();
        };

        try {
          if (!this.algolia.baseUrl || !this.algolia.indexName || !this.algolia.apiKey) {
            throw new Error('Algolia search is not configured');
          }

          const response = await fetch(
            `${this.algolia.baseUrl}/1/indexes/${this.algolia.indexName}/query`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Algolia-Application-Id': this.algolia.applicationId,
                'X-Algolia-API-Key': this.algolia.apiKey,
              },
              body: JSON.stringify({ query: this.searchQuery, hitsPerPage: 1000 }),
            },
          );

          if (!response.ok) throw new Error(`Algolia API error: ${response.status}`);

          const data = await response.json();
          const hits = data.hits || [];
          if (hits.length) {
            loadResults(hits);
          } else {
            console.warn('Algolia returned no products; falling back to Shopify search.');
            loadResults(await this.searchShopifyFullFallback(this.searchQuery));
          }
        } catch (error) {
          console.warn('Algolia search unavailable; falling back to Shopify search.', error);
          try {
            loadResults(await this.searchShopifyFullFallback(this.searchQuery));
          } catch (fallbackError) {
            console.error('Shopify fallback search error:', fallbackError);
            this.allResults = [];
            this.filteredResults = [];
            this.paginatedResults = [];
          }
        }

        this.isLoading = false;
      },

      toggleCategory(categoryValue) {
        const index = this.selectedCategories.indexOf(categoryValue);
        if (index > -1) this.selectedCategories.splice(index, 1);
        else this.selectedCategories.push(categoryValue);
        this.currentPage = 0;
        this.applyFiltersAndSort();
      },

      clearCategoryFilters() {
        this.selectedCategories = [];
        this.currentPage = 0;
        this.applyFiltersAndSort();
      },

      isCategorySelected(categoryValue) {
        return this.selectedCategories.includes(categoryValue);
      },

      toggleTagFilter(prefix, value) {
        const selected = this.tagFilters[prefix].selected;
        const index = selected.indexOf(value);
        if (index > -1) selected.splice(index, 1);
        else selected.push(value);
        this.currentPage = 0;
        this.applyFiltersAndSort();
      },

      clearTagFilters(prefix) {
        this.tagFilters[prefix].selected = [];
        this.currentPage = 0;
        this.applyFiltersAndSort();
      },

      isTagFilterSelected(prefix, value) {
        return this.tagFilters[prefix]?.selected.includes(value) || false;
      },

      getAllFilterGroups() {
        const groups = [];

        groups.push({
          key: 'category',
          name: 'Produktart',
          options: this.categories,
          visible: true,
          hasActiveFilters: this.selectedCategories.length > 0,
          toggleFunction: (value) => this.toggleCategory(value),
          isSelectedFunction: (value) => this.isCategorySelected(value),
          clearFunction: () => this.clearCategoryFilters(),
          type: 'category',
        });

        Object.entries(this.tagFilters).forEach(([prefix, tagFilter]) => {
          if (!tagFilter.options || tagFilter.options.length === 0) return;
          groups.push({
            key: prefix,
            name: tagFilter.name,
            visible: true,
            options: tagFilter.options,
            hasActiveFilters: tagFilter.selected.length > 0,
            toggleFunction: (value) => this.toggleTagFilter(prefix, value),
            isSelectedFunction: (value) => this.isTagFilterSelected(prefix, value),
            clearFunction: () => this.clearTagFilters(prefix),
            type: 'tag',
          });
        });

        Object.keys(this.filters).forEach(filterKey => {
          const filter = this.filters[filterKey];
          groups.push({
            key: filterKey,
            name: filter.name,
            visible: true,
            options: filter.options,
            hasActiveFilters: filter.options.some(o => o.checked),
            toggleFunction: null,
            isSelectedFunction: null,
            clearFunction: () => filter.options.forEach(o => { o.checked = false; }),
            type: 'standard',
          });
        });

        return groups;
      },
    }),
  );
});
